import React from "react";
import PropTypes from "prop-types";
import { Card, Box, Typography, Grid, Stack } from "@mui/material";
import { Newspaper, Sparkles } from "lucide-react";
import { colors, borderRadiusPx, customShadows } from "../../../../../theme";

const NewsHero = ({
  totalArticlesCount = 0,
  featuredCount = 0,
  latestUpdateTime = "Recently",
}) => {
  return (
    <Card
      sx={{
        p: { xs: 3, sm: 4 },
        borderRadius: borderRadiusPx.xl,
        backgroundColor: colors.dashboard.cardBg,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardFlat,
        position: "relative",
        overflow: "hidden",
        mb: 4,
      }}
    >
      {/* Top Gradient Accent Stripe */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: colors.gradients.primary,
        }}
      />

      <Grid container spacing={3} alignItems="center">
        {/* Left Section: Title & Subtitle */}
        <Grid item xs={12} lg={7}>
          <Stack spacing={1.5}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 900,
                color: colors.slate[900],
                letterSpacing: "-0.5px",
                fontSize: { xs: "1.85rem", sm: "2.35rem", md: "2.6rem" },
                textAlign: "left",
              }}
            >
              News & Platform Updates
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: colors.slate[600],
                fontSize: { xs: "0.925rem", sm: "1rem" },
                maxWidth: 620,
                lineHeight: 1.6,
              }}
            >
              Stay informed with official Autobet protocol releases, major
              jackpot milestones, game launches, partnership highlights, and
              transparent on-chain development logs.
            </Typography>
          </Stack>
        </Grid>

        {/* Right Section: Hero Metric Stats */}
        <Grid item xs={12} lg={5}>
          <Grid container spacing={2}>
            {/* Stat 1: Total Stories */}
            <Grid item xs={6}>
              <Box
                className="news-metric-card"
                sx={{
                  p: 2.5,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.slate[50],
                  border: `1px solid ${colors.slate[200]}`,
                  height: "100%",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.blue[50],
                      color: colors.primary.main,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Newspaper size={18} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[600],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.72rem",
                    }}
                  >
                    All Articles
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    color: colors.slate[900],
                    fontSize: "1.65rem",
                  }}
                >
                  {totalArticlesCount}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: colors.emerald[600], fontWeight: 600 }}
                >
                  Published to date
                </Typography>
              </Box>
            </Grid>

            {/* Stat 2: Featured Articles / Latest Activity */}
            <Grid item xs={6}>
              <Box
                className="news-metric-card"
                sx={{
                  p: 2.5,
                  borderRadius: borderRadiusPx.lg,
                  bgcolor: colors.blue[50],
                  border: `1px solid ${colors.blue[200]}`,
                  height: "100%",
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: borderRadiusPx.md,
                      bgcolor: colors.primary.main,
                      color: colors.neutral.white,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Sparkles size={18} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: colors.slate[600],
                      fontWeight: 700,
                      textTransform: "uppercase",
                      fontSize: "0.72rem",
                    }}
                  >
                    Featured Stories
                  </Typography>
                </Stack>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    color: colors.primary.dark,
                    fontSize: "1.65rem",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {featuredCount}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: colors.primary.main, fontWeight: 600 }}
                >
                  {latestUpdateTime}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Card>
  );
};

NewsHero.propTypes = {
  totalArticlesCount: PropTypes.number,
  featuredCount: PropTypes.number,
  latestUpdateTime: PropTypes.string,
};

export default NewsHero;
